import type { MeResponse, Offer, ProductDetail, RetailerPrice } from '@stalvi/contracts';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { Header } from '../components/Header';
import { ProductThumb } from '../components/ProductThumb';
import { ApiError, api } from '../lib/api';
import { formatCLP, formatRelativeDate, isStale } from '../lib/format';

export function Product({ me, onSignOut }: { me: MeResponse; onSignOut: () => void }) {
  const { productId } = useParams();
  const nav = useNavigate();
  const { data, isLoading, error } = useQuery<ProductDetail, ApiError>({
    queryKey: ['product', productId],
    queryFn: () => api.product(productId ?? ''),
    retry: (failureCount, err) => (err instanceof ApiError && err.status === 404 ? false : failureCount < 1),
  });

  return (
    <div className="stalvi-app flex flex-col">
      <Header me={me} onSignOut={onSignOut} />
      <main className="flex-1 px-6 py-6 overflow-auto">
        <div className="max-w-6xl mx-auto">
          <button type="button" className="btn btn-secondary btn-sm mb-4" onClick={() => nav('/')}>
            Buscar otro producto
          </button>

          {isLoading && <div className="card p-8">Cargando…</div>}

          {error && error.status === 404 && (
            <div className="card p-8 text-center">
              <p className="mb-4">No encontramos el producto solicitado</p>
              <button type="button" className="btn btn-primary" onClick={() => nav('/')}>
                Volver al buscador
              </button>
            </div>
          )}

          {data && (
            <div className="grid lg:grid-cols-2 gap-6">
              <ProductSummary data={data} />
              <PriceSection prices={data.prices} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function ProductSummary({ data }: { data: ProductDetail }) {
  return (
    <section className="card p-6">
      <h1 className="text-2xl font-bold mb-1">{data.name}</h1>
      <p className="text-text-secondary mb-1">{data.brand ?? '—'}</p>
      {data.ean13 && <p className="mono text-xs text-text-tertiary mb-4">{data.ean13}</p>}
      <div className="w-[280px] h-[280px] mb-4">
        <ProductThumb src={data.imageUrl ?? null} alt={data.name} size="full" />
      </div>
      {data.category && <p className="text-sm text-text-secondary mb-2">{data.category.label}</p>}
      {data.packaging && (
        <p className="mb-2">
          <strong>Envase:</strong> {data.packaging}
        </p>
      )}
      {data.description && (
        <div
          className="prose prose-sm max-w-none mt-4"
          // Server sanitizes the description HTML before sending it.
          // eslint-disable-next-line @typescript-eslint/naming-convention
          dangerouslySetInnerHTML={{ __html: data.description }}
        />
      )}
      {data.nutritionFacts.length > 0 && (
        <table className="tbl mt-6">
          <caption>Información nutricional</caption>
          <thead>
            <tr>
              <th scope="col">Nutriente</th>
              <th scope="col">Valor</th>
              <th scope="col">Unidad</th>
            </tr>
          </thead>
          <tbody>
            {data.nutritionFacts.map((r) => (
              <tr key={`${r.nutrient}-${r.value}-${r.unit}`}>
                <td>{r.nutrient}</td>
                <td className="mono">{r.value}</td>
                <td className="text-text-secondary">{r.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function PriceSection({ prices }: { prices: RetailerPrice[] }) {
  return (
    <section className="card p-6">
      <h2 className="text-lg font-semibold mb-4">Precios por cadena</h2>
      {prices.length === 0 ? (
        <p className="text-text-secondary">Sin precios disponibles.</p>
      ) : (
        <ul className="flex flex-col gap-4">
          {prices.map((p, i) => (
            <RetailerCard
              // prices[] is cheapest-first; prices[0] is the lowest.
              key={p.retailerId ?? p.retailerName ?? `retailer-${i}`}
              price={p}
              isLowest={i === 0 && prices.length > 1}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function RetailerCard({ price, isLowest }: { price: RetailerPrice; isLowest: boolean }) {
  const stale = isStale(price.observedAt);
  const hasStrike = price.listPrice != null && price.bestPrice != null && price.listPrice > price.bestPrice;
  return (
    <li className="border border-border-subtle rounded-md p-4" data-testid="retailer-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-medium truncate">{price.retailerName ?? '—'}</p>
          <AvailabilityBadge available={price.available} />
        </div>
        <div className="text-right shrink-0">
          <div className="flex items-center justify-end gap-2">
            {hasStrike && price.listPrice != null && (
              <span className="mono text-sm text-text-tertiary line-through">
                {formatCLP(price.listPrice)}
              </span>
            )}
            <span className="mono text-lg font-semibold">
              {price.bestPrice != null ? formatCLP(price.bestPrice) : '—'}
            </span>
          </div>
          {isLowest && <span className="pill pill-success mt-1 inline-block">Más bajo</span>}
        </div>
      </div>

      <div className="mt-2 text-sm text-text-secondary flex items-center gap-2">
        <span>{formatRelativeDate(price.observedAt)}</span>
        {stale && <span className="pill pill-neutral">Desactualizado</span>}
      </div>

      {price.offers.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {price.offers.map((offer, i) => (
            <OfferRow key={`${offer.offerType ?? 'offer'}-${i}`} offer={offer} />
          ))}
        </ul>
      )}
    </li>
  );
}

function AvailabilityBadge({ available }: { available: boolean | null }) {
  if (available == null) return null;
  return available ? (
    <span className="pill pill-success text-xs mt-1 inline-block">Disponible</span>
  ) : (
    <span className="pill pill-neutral text-xs mt-1 inline-block">Sin stock</span>
  );
}

// Renders a currently-valid conditional offer. Every field is nullable, so each
// fragment is null-guarded; the row collapses to nothing visible if all empty.
function OfferRow({ offer }: { offer: Offer }) {
  const label = offer.promoName ?? offer.promoTag ?? offer.offerType ?? 'Promoción';
  const qty = offer.minQty != null && offer.paidQty != null ? `${offer.minQty}x${offer.paidQty}` : null;
  const amount =
    offer.totalPrice != null
      ? formatCLP(offer.totalPrice)
      : offer.unitPrice != null
        ? `${formatCLP(offer.unitPrice)} c/u`
        : null;
  const conditions = [offer.paymentCondition, offer.membershipCondition].filter(Boolean);

  return (
    <li className="text-sm rounded bg-bg-subtle px-3 py-2" data-testid="offer-row">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-text-primary">{label}</span>
        {amount && <span className="mono">{amount}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-tertiary mt-1">
        {qty && <span className="pill pill-neutral">{qty}</span>}
        {conditions.map((c) => (
          <span key={c}>{c}</span>
        ))}
        {offer.validTo && <span>hasta {formatRelativeDate(offer.validTo)}</span>}
      </div>
    </li>
  );
}
